import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

class EbookFreeGift extends HTMLElement {
  #isAddingGift = false;

  connectedCallback() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
  }

  #handleCartUpdate = (event) => {
    if (
      this.#isAddingGift ||
      this.dataset.autoAddEnabled !== 'true' ||
      event.action !== 'add' ||
      !event.promise
    )
      return;

    event.promise
      .then(({ detail }) => {
        if (detail?.didError || detail?.source === 'ebook-free-gift') return;
        if (String(detail?.productId ?? '') !== String(this.dataset.mainProductId ?? '')) return;
        return this.#addGiftIfNeeded();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ebook-free-gift] Cart event failed:', error);
      });
  };

  async #addGiftIfNeeded() {
    const giftVariantId = Number(this.dataset.giftVariantId);
    if (!giftVariantId || this.dataset.giftAvailable !== 'true') return;

    this.#isAddingGift = true;
    let deferredEvent;

    try {
      const currentCartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!currentCartResponse.ok) throw new Error('Cart could not be loaded');

      const currentCart = await currentCartResponse.json();
      const giftAlreadyInCart = currentCart.items?.some((item) => Number(item.variant_id) === giftVariantId);
      if (giftAlreadyInCart) return;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = [];
      for (const component of cartItemsComponents) {
        if (component instanceof HTMLElement && component.dataset.sectionId) {
          sectionIds.push(component.dataset.sectionId);
        }
      }

      const formData = new FormData();
      formData.set('id', String(giftVariantId));
      formData.set('quantity', '1');
      if (sectionIds.length) formData.set('sections', sectionIds.join(','));

      deferredEvent = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(giftVariantId), quantity: 1 }],
          promise: deferredEvent.promise,
        })
      );

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      const addResponse = await response.json();
      if (!response.ok || addResponse.status) throw new Error(addResponse.description || 'Gift could not be added');

      const refreshedCartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      const refreshedCart = await refreshedCartResponse.json();

      deferredEvent.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(refreshedCart),
        detail: {
          items: refreshedCart.items,
          source: 'ebook-free-gift',
          sourceId: this.id || 'ebook-free-gift',
          itemCount: 1,
          productId: this.dataset.giftProductId,
          sections: addResponse.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferredEvent?.reject(error);
      console.error('[ebook-free-gift]', error);
    } finally {
      this.#isAddingGift = false;
    }
  }
}

if (!customElements.get('ebook-free-gift')) {
  customElements.define('ebook-free-gift', EbookFreeGift);
}
